import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { Customer } from '../customer';
import { GoogleMapsComponent } from '../google-maps/google-maps.component';
import {
    AirtableArea,
    AirtableContract,
    AirtableService,
    AreaType,
    GeoJsonFeature,
    GeoJsonPosition,
} from '../services/airtable/airtable.service';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';
import { PageTitleService } from '../services/page-title/page-title.service';

enum AreaFilter {
    All = 'all',
    Gemeenten = 'gemeenten',
    Provincies = 'provincies',
    Waterschappen = 'waterschappen',
}

@Component({
    selector: 'app-airtable-map',
    templateUrl: './airtable-map.component.html',
    styleUrls: ['./airtable-map.component.scss'],
    standalone: false,
})
export class AirtableMapComponent extends GoogleMapsComponent implements OnInit, OnDestroy {
    customer: Customer = {} as Customer;

    polygons: MapPolygon[] = [];
    filteredPolygons: MapPolygon[] = [];

    areaFilter = AreaFilter;
    areaFilterOptions: { value: AreaFilter; label: string }[] = [
        { value: AreaFilter.All, label: 'Alle gebieden' },
        { value: AreaFilter.Gemeenten, label: 'Gemeenten' },
        { value: AreaFilter.Provincies, label: 'Provincies' },
        { value: AreaFilter.Waterschappen, label: 'Waterschappen' },
    ];

    loading = false;
    statussen: { name: string; color: string }[] = [];

    screenshot = false;
    selectedCustomer: AirtableArea | null = null;
    selectedAreaName: string | null = null;
    firstContract: Partial<AirtableContract> = {};
    mobileMenuOpen = false;

    fcFilter = new FormControl<AreaFilter>(AreaFilter.All, { nonNullable: true });

    constructor(
        private airtableService: AirtableService,
        private router: Router,
        private ngZone: NgZone,
        protected confirmDialogService: ConfirmDialogService,
        protected cdref: ChangeDetectorRef,
        private pageTitleService: PageTitleService
    ) {
        super(confirmDialogService, cdref);
    }

    ngOnInit() {
        this.pageTitleService.setTitle('Commercie');
        super.ngOnInit();
        this.options.mapId = '721b75f40e9d44452e7d2b8e';

        this.subscriptions.add(this.fcFilter.valueChanges.subscribe(() => this.filterPolygons()));

        this.statussen = [
            'Onderhanden werk',
            'Lopende Aanbestedingen',
            'Niet-potentieel/on-hold',
            'Inventariseren/analyseren 2026',
            'Potentieel',
            'Kansrijke aanbestedingen',
        ].map(status => ({
            name: status,
            color: this.getColor(status, 1),
        }));

        this.loading = true;

        this.subscriptions.add(
            combineLatest([
                this.airtableService.getTable(),
                this.airtableService.getGemeenten(),
                this.airtableService.getProvincies(),
                this.airtableService.getWaterschappen(),
            ]).subscribe(([airtable, gemeenten, provincies, waterschappen]) => {
                this.polygons = [];

                airtable.data.forEach(area => {
                    const features = this.findFeatures(
                        area,
                        gemeenten.data.features,
                        provincies.data.features,
                        waterschappen.data.features
                    );

                    if (features.length) {
                        features.forEach(feature =>
                            this.getRings(feature).forEach(ring => {
                                const exteriorRing = ring
                                    .map(point => ({
                                        lng: Number(point[0]),
                                        lat: Number(point[1]),
                                    }))
                                    .filter(point => Number.isFinite(point.lng) && Number.isFinite(point.lat));

                                if (exteriorRing.length < 3) {
                                    console.warn('Skipping invalid ring', {
                                        areaCode: area.areaCode,
                                        geoJsonKey: area.geoJsonKey,
                                        areaType: area.areaType,
                                        title: area.title,
                                        name: area.name,
                                        feature,
                                    });
                                    return;
                                }

                                this.polygons.push({
                                    exteriorRing,
                                    fillColor: this.getColor(area.status, 0.7),
                                    strokeColor: this.getColor(area.status, 1),
                                    strokeWeight: area.areaType === 'gemeente' ? 1 : 3,
                                    data: area,
                                    areaCode: area.areaCode,
                                    geoJsonKey: area.geoJsonKey ?? area.areaCode,
                                    areaType: area.areaType,
                                    areaName: this.getAreaName(area),
                                });
                            })
                        );
                    } else {
                        console.log('Could not find geojson feature', {
                            areaCode: area.areaCode,
                            geoJsonKey: area.geoJsonKey,
                            areaType: area.areaType,
                            status: area.status,
                            title: area.title,
                            name: area.name,
                        });
                        return;
                    }
                });

                this.filterPolygons();
                this.loading = false;
            })
        );

        this.centerCountry();
    }

    private findFeatures(
        area: AirtableArea,
        gemeenten: GeoJsonFeature[],
        provincies: GeoJsonFeature[],
        waterschappen: GeoJsonFeature[]
    ): GeoJsonFeature[] {
        const geoJsonKey = (area.geoJsonKey ?? area.areaCode ?? '').toUpperCase();

        if (!geoJsonKey) {
            return [];
        }

        if (area.areaType === 'gemeente') {
            return gemeenten.filter(
                feature => feature.properties.GM_CODE?.toUpperCase() === geoJsonKey && feature.properties.H2O === 'NEE'
            );
        }

        if (area.areaType === 'provincie') {
            return provincies.filter(feature => feature.properties.statcode?.toUpperCase() === geoJsonKey);
        }

        if (area.areaType === 'waterschap') {
            return waterschappen.filter(feature => feature.properties.statcode?.toUpperCase() === geoJsonKey);
        }

        return [];
    }

    private getRings(feature: GeoJsonFeature): GeoJsonPosition[][] {
        const geometry = feature.geometry;

        if (geometry.type === 'Polygon') {
            return geometry.coordinates as GeoJsonPosition[][];
        }

        if (geometry.type === 'MultiPolygon') {
            return (geometry.coordinates as GeoJsonPosition[][][]).flat();
        }

        console.warn('Unknown geometry structure', feature);
        return [];
    }

    private getAreaName(area: AirtableArea): string {
        const name = area.name ?? area.title ?? area.areaCode;

        if (area.areaType === 'gemeente') {
            return `Gemeente ${name}`;
        }

        return name;
    }

    private filterPolygons() {
        this.filteredPolygons = this.polygons.filter(polygon => {
            return (
                this.fcFilter.value === AreaFilter.All ||
                (this.fcFilter.value === AreaFilter.Gemeenten && polygon.areaType === 'gemeente') ||
                (this.fcFilter.value === AreaFilter.Provincies && polygon.areaType === 'provincie') ||
                (this.fcFilter.value === AreaFilter.Waterschappen && polygon.areaType === 'waterschap')
            );
        });
        this.selectedCustomer = null;
        this.selectedAreaName = null;
        this.firstContract = {};
    }

    private getColor(status: string | null | undefined, opacity: number) {
        if (status === 'Onderhanden werk') {
            return `rgba(40, 40, 140, ${opacity})`;
        }
        if (status === 'Lopende Aanbestedingen') {
            return `rgba(255, 130, 132, ${opacity})`;
        }
        if (status === 'Niet-potentieel/on-hold') {
            return `rgba(150, 150, 150, ${opacity})`;
        }
        if (status === 'Inventariseren/analyseren 2026') {
            return `rgba(146, 197, 222, ${opacity})`;
        }
        if (status === 'Potentieel') {
            return `rgba(67, 156, 199, ${opacity})`;
        }
        if (['Kansrijke aanbestedingen', 'Lopende Aanbesteding'].includes(status)) {
            return `rgba(255, 255, 0, ${opacity})`;
        }
        console.log('Onbekende status: ', status);
        return `rgba(220, 239, 250, ${opacity})`;
    }

    public polygonClick(event: MapPolygon) {
        this.ngZone.run(() => {
            this.selectedCustomer = event.data;
            this.selectedAreaName = event.areaName;
            this.firstContract = event.data.contracts?.[0] ?? {};
        });
    }

    goHome() {
        this.router.navigate([this.customer.consumer_customer ? '' : this.Routenames.customers], { replaceUrl: true });
    }
}

export interface MapPolygon {
    exteriorRing: { lng: number; lat: number }[];
    fillColor: string;
    strokeColor: string;
    strokeWeight: number;
    data: AirtableArea;
    areaCode: string;
    geoJsonKey: string;
    areaType: AreaType;
    areaName: string;
}
